import React, { useEffect, useState, useCallback, useRef } from "react";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TextField,
  TableBody,
  TablePagination,
  IconButton,
  Menu,
  Checkbox,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import { useClientInfo, axiosInstance } from "../../utils/commonImports";

import Alert from "@mui/material/Alert";
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Print as PrintIcon,
} from "@mui/icons-material";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import dayjs from "dayjs";

import InvoiceFormLocal from "./InvoiceFormLocal";
import InvoiceFormContract from "./InvoiceFormContract";
import InvoiceFormMisc from "./InvoiceFormMisc";

import PreviewModal from "../../components/preview/PreviewModal";
import { numberToIndianWords } from "../../utils/numberToIndianWords";
import { normalizeDateAny } from "../../utils/dateUtils";

const STATUS_FILTERS = ["All", "Sent", "Printed", "Cancelled", "Deleted"];

export default function InvoiceForm() {
  const tableRef = useRef();
  const { clientId } = useClientInfo();
  // Data states
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [clients, setClients] = useState([]);
  const [years, setYears] = useState([]);

  // Filters states
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterInvoiceNumber, setFilterInvoiceNumber] = useState("");
  const [filterFromDate, setFilterFromDate] = useState(null);
  const [filterToDate, setFilterToDate] = useState(null);
  const [filterCustomer, setFilterCustomer] = useState("");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(13);

  // Edit/Add states
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);

  // Menu and Snackbar
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  // Print preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHTML, setPreviewHTML] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);

  const [anchorElAdd, setAnchorElAdd] = useState(null);

  // Fetch dropdowns for filters (similar to TripsheetForm)
  const fetchDropdowns = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await axiosInstance.get("/api/customers", {
        params: { client_id: clientId },
      });
      setCustomers(res.data || []);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch dropdowns.",
        severity: "error",
      });
    }
  }, [clientId]);

  const fetchInvoices = useCallback(async () => {
    if (!clientId) return; // also add a guard, consistent with your other files
    try {
      const params = new URLSearchParams();
      params.append("client_id", clientId);
      if (filterStatus !== "All") params.append("status", filterStatus);
      if (filterInvoiceNumber)
        params.append("invoice_number", filterInvoiceNumber);
      if (filterFromDate)
        params.append("from_date", dayjs(filterFromDate).format("YYYY-MM-DD"));
      if (filterToDate)
        params.append("to_date", dayjs(filterToDate).format("YYYY-MM-DD"));
      if (filterCustomer) params.append("customer_id", filterCustomer);

      const res = await axiosInstance.get(`/api/invoices?${params.toString()}`);
      setInvoices(res.data);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch invoice records.",
        severity: "error",
      });
    }
  }, [
    clientId, // ← added
    filterStatus,
    filterInvoiceNumber,
    filterFromDate,
    filterToDate,
    filterCustomer,
  ]);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Pagination handlers
  const handleChangePage = (_event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Menu handlers
  const handleRowMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };
  const handleRowMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  function mapTripDuty(t) {
    function showDash(val) {
      if (val === null || val === undefined) return "-";
      if (val === "") return "-";
      if (isNaN(val)) return "-";
      return Number(val);
    }
    function showNumber(val) {
      return val && !isNaN(val) ? Number(val) : 0;
    }
    function showAmount(val) {
      const num = Number(val);
      return num > 0 ? num : "-";
    }
    function formatSlabDisplay(item) {
      const itemDuty = (item.duty || "").trim();
      if (itemDuty === "Outstation" || itemDuty === "OSDayRate") {
        const days = showNumber(item.trip_charges_days_used);
        const minKm = showNumber(item.trip_charges_slab_km);
        return days > 0 && minKm > 0 ? `${days} / ${days * minKm}` : "-";
      }
      const hours = showNumber(item.trip_charges_slab_hour);
      const kms = showNumber(item.trip_charges_slab_km);
      return hours > 0 || kms > 0 ? `${hours} / ${kms}` : "-";
    }

    let dutyVal = (t.duty || "").trim();
    let batta_local =
      dutyVal === "Local" ? showNumber(t.trip_charges_local_batta) : 0;
    let batta_os_day =
      dutyVal !== "Local"
        ? showNumber(t.trip_charges_os_batta_total_amount)
        : 0;
    let batta_os_night =
      dutyVal !== "Local"
        ? showNumber(t.trip_charges_os_night_batta_total_amount)
        : 0;
    let batta_main = batta_local + batta_os_day + batta_os_night;

    const extra_hour = showNumber(t.trip_charges_extra_hour);
    const extra_hour_rate = showNumber(t.trip_charges_slab_extra_hour_rate);
    const extra_km = showNumber(t.trip_charges_extra_kms);
    const extra_km_rate = showNumber(t.trip_charges_slab_extra_km_rate);

    const displayExtraHours =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : extra_hour > 0 && extra_hour_rate > 0
          ? `${extra_hour} × ${extra_hour_rate}`
          : "-";
    const displayExtraHoursAmt =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : showAmount(t.trip_charges_extra_hour_amount);
    const displayExtraKms =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : extra_km > 0 && extra_km_rate > 0
          ? `${extra_km} × ${extra_km_rate}`
          : "-";
    const displayExtraKmsAmt =
      dutyVal === "Outstation" || dutyVal === "OSDayRate"
        ? "-"
        : showAmount(t.trip_charges_extra_km_amount);

    const parkingNum = showNumber(t.trip_charges_parking);
    const tollNum = showNumber(t.trip_charges_toll_fasttag);
    const permitNum = showNumber(t.trip_charges_permit);
    const othersNum = showNumber(t.trip_charges_others);

    return {
      serial: t.serial,
      duty: t.duty,
      tripsheet_number: t.tripsheet_number,
      open_date: t.open_date
        ? new Date(normalizeDateAny(t.open_date)).toLocaleDateString("en-GB")
        : "-",
      vehicle_no: t.is_vendor_tripsheet ? t.vendor_vehicle_no : t.vehicleid,
      vehicle_type: t.assigned_vehicle_group_name || "-",
      days: showDash(t.trip_charges_days_used),
      hours: showDash(t.trip_charges_hours_used),
      kms: showDash(t.trip_charges_kms_used),
      slab_display: formatSlabDisplay(t),
      slab_rate: showNumber(
        dutyVal === "Outstation"
          ? t.trip_charges_slab_os_rate
          : t.trip_charges_slab_rate,
      ),
      slab_amount: showNumber(t.trip_charges_charge_amount),
      extra_hour_amount: showNumber(t.trip_charges_extra_hour_amount),
      extra_km_amount: showNumber(t.trip_charges_extra_km_amount),
      extra_hours_display: displayExtraHours,
      extra_hour_amount_display: displayExtraHoursAmt,
      extra_kms_display: displayExtraKms,
      extra_km_amount_display: displayExtraKmsAmt,
      batta_local: showNumber(batta_local),
      batta_os_day: showNumber(batta_os_day),
      batta_os_night: showNumber(batta_os_night),
      nbatta: showNumber(batta_os_night),
      batta_main: showNumber(batta_main),
      parking: parkingNum,
      toll: tollNum,
      permit: permitNum,
      others: othersNum,
      escort_amount: showNumber(t.trip_charges_escort_amount),
      advance:
        showNumber(t.trip_charges_advance) +
        showNumber(t.trip_charges_booking_advance),
      trip_net_amount:
        showNumber(t.trip_charges_charge_amount) +
        showNumber(t.trip_charges_extra_hour_amount) +
        showNumber(t.trip_charges_extra_km_amount) +
        showNumber(batta_main) +
        parkingNum +
        tollNum +
        permitNum +
        othersNum +
        showNumber(t.trip_charges_escort_amount),
      trip_advance:
        showNumber(t.trip_charges_advance) +
        showNumber(t.trip_charges_booking_advance),
    };
  }

  function mapBankDetails(clientBanks) {
    const bank = clientBanks.find((b) => b.is_primary) || clientBanks[0] || {};
    return {
      bank_name: bank.bank_name || "",
      account_number: bank.account_number || "",
      ifsc: bank.ifsc_code || "",
      branch: bank.branch || "",
      account_holder: bank.account_holder_name || "",
    };
  }


  function mapContractDuty(t) {
  function showDash(val) {
    if (val === null || val === undefined) return "-";
    if (val === "") return "-";
    if (isNaN(val)) return "-";
    return Number(val);
  }
  function showNumber(val) {
    return val && !isNaN(val) ? Number(val) : 0;
  }
  function toHHMM(timeStr) {
    if (!timeStr) return "-";
    return timeStr.toString().substring(0, 5);
  }

  let dutyVal = (t.duty || "").trim();
  let batta_local = dutyVal === "Contract" ? showNumber(t.trip_charges_local_batta) : 0;
  let batta_os_day = dutyVal !== "Contract" ? showNumber(t.trip_charges_os_batta_total_amount) : 0;
  let batta_os_night = dutyVal !== "Contract" ? showNumber(t.trip_charges_os_night_batta_total_amount) : 0;
  let batta_main = batta_local + batta_os_day + batta_os_night;

  const parkingNum = showNumber(t.trip_charges_parking);
  const tollNum = showNumber(t.trip_charges_toll_fasttag);
  const permitNum = showNumber(t.trip_charges_permit);
  const othersNum = showNumber(t.trip_charges_others);

  return {
    serial: t.serial,
    duty: t.duty,
    tripsheet_number: t.tripsheet_number,
    open_date: t.open_date
      ? new Date(normalizeDateAny(t.open_date)).toLocaleDateString("en-GB")
      : "-",
    close_date: t.close_date
      ? new Date(normalizeDateAny(t.close_date)).toLocaleDateString("en-GB")
      : "-",
    vehicle_no: t.is_vendor_tripsheet ? t.vendor_vehicle_no : t.vehicleid,
    vehicle_type: t.assigned_vehicle_group_name || "-",
    open_time: toHHMM(t.open_time),
    close_time: toHHMM(t.close_time),
    open_km_reading: t.open_km_reading ?? "-",
    close_km_reading: t.close_km_reading ?? "-",
    hours_used: t.trip_charges_hours_used ?? t.hours ?? "-",
    kms_used: t.trip_charges_kms_used ?? t.kms ?? "-",
    fuel: showNumber(t.trip_charges_fuel_esculation_amount),
    escort: showNumber(t.trip_charges_escort_amount),
    days: showDash(t.trip_charges_days_used),
    hours: showDash(t.trip_charges_hours_used),
    kms: showDash(t.trip_charges_kms_used),
    slab_amount: showNumber(t.trip_charges_charge_amount),
    extra_hour_amount: showNumber(t.trip_charges_extra_hour_amount),
    extra_km_amount: showNumber(t.trip_charges_extra_km_amount),
    batta_local: showNumber(batta_local),
    batta_os_day: showNumber(batta_os_day),
    batta_os_night: showNumber(batta_os_night),
    nbatta: showNumber(batta_os_night),
    batta_main: showNumber(batta_main),
    parking: parkingNum,
    toll: tollNum,
    permit: permitNum,
    others: othersNum,
    escort_amount: showNumber(t.trip_charges_escort_amount),
    advance:
      showNumber(t.trip_charges_advance) + showNumber(t.trip_charges_booking_advance),
    trip_advance:
      showNumber(t.trip_charges_advance) + showNumber(t.trip_charges_booking_advance),
  };
}

  // Delete handler
  const handleDelete = async (row) => {
    handleRowMenuClose();
    try {
      await axiosInstance.delete(`/api/invoices/${row.invoiceid}`, {
        params: { client_id: clientId },
      });
      setSnackbar({
        open: true,
        msg: "Invoice deleted",
        severity: "success",
      });
      fetchInvoices();
    } catch (err) {
      setSnackbar({ open: true, msg: "Delete failed", severity: "error" });
    }
  };

  const handleEdit = async (row) => {
    try {
      if (!row.invoiceid) {
        setSnackbar({
          open: true,
          msg: "Invalid invoice selected: missing invoice ID",
          severity: "error",
        });
        handleRowMenuClose();
        return;
      }

      const res = await axiosInstance.get(`/api/invoices/${row.invoiceid}`, {
        params: { client_id: clientId },
      });

      // Determine invoice type
      let type = "local";
      if (row.bill_type === "Contract") type = "contract";
      else if (row.bill_type === "Misc") type = "misc";

      setShowForm({
        type,
        data: res.data,
      });
    } catch (err) {
      console.error("Error fetching invoice details:", err.response || err);
      setSnackbar({
        open: true,
        msg: `Failed to fetch invoice details: ${
          err.response?.data?.message || err.message || "Unknown error"
        }`,
        severity: "error",
      });
    }

    handleRowMenuClose();
  };

const handlePrint = async (row) => {
  handleRowMenuClose();

  if (!row?.invoiceid) {
    setSnackbar({ open: true, msg: "Invalid invoice selected", severity: "error" });
    return;
  }

  const billType = row.bill_type;

  if (billType === "Misc") {
    setSnackbar({
      open: true,
      msg: `Print preview for "Misc" invoices isn't wired up yet.`,
      severity: "warning",
    });
    return;
  }

  setPrintLoading(true);
  try {
    // Shared fetches
    const invoiceRes = await axiosInstance.get(`/api/invoices/${row.invoiceid}`, {
      params: { client_id: clientId },
    });
    const invoice = invoiceRes.data;

    const clientRes = await axiosInstance.get(`/api/clients/${clientId}`);
    const client = clientRes.data.client;

    let customerList = customers;
    if (!customerList.length) {
      const custRes = await axiosInstance.get("/api/customers", {
        params: { client_id: clientId },
      });
      customerList = custRes.data || [];
    }
    const customerDetails = customerList.find(
      (c) => c.customerid == invoice.customer_id,
    );

    const bankRes = await axiosInstance.get(`/api/client-banks/${clientId}`);
    const bank = mapBankDetails(bankRes.data || []);

    let termsList = [];
    try {
      const termsRes = await axiosInstance.get("/api/terms", {
        params: { client_id: clientId, document_type: "invoice" },
      });
      termsList = termsRes.data?.terms || [];
    } catch (e) {
      termsList = [];
    }

    if (billType === "Contract") {
      // ---- CONTRACT-SPECIFIC DATA ----
      let invoiceContract = null;
      try {
        const contractRes = await axiosInstance.get(
          `/api/invoices/contract/${row.invoiceid}`,
          { params: { client_id: clientId } },
        );
        invoiceContract = contractRes.data?.contract || null;
      } catch (e) {
        console.error("Failed to load invoice contract:", e);
      }

      if (!invoiceContract) {
        setSnackbar({
          open: true,
          msg: "No contract details found for this invoice",
          severity: "error",
        });
        setPrintLoading(false);
        return;
      }

      const tripsRes = await axiosInstance.get(
        `/api/invoices/${row.invoiceid}/contractduties`,
        {
          params: {
            client_id: clientId,
            customer_id: invoice.customer_id,
            vehicle_group_id: invoiceContract.vehicle_group_id,
          },
        },
      );
      const payload = tripsRes.data;
      const selectedArray = Array.isArray(payload)
        ? payload
        : payload?.selected || [];
      const mappedTrips = selectedArray.map((t, i) =>
        mapContractDuty({ ...t, serial: i + 1 }),
      );

      let basicTotal = 0,
        battaTotal = 0,
        parkingTotal = 0,
        tollTotal = 0,
        permitTotal = 0,
        othersTotal = 0,
        escortTotal = 0,
        advanceTotal = 0,
        grossTotal = 0;

      mappedTrips.forEach((t) => {
        const basic =
          Number(t.slab_amount || 0) +
          Number(t.extra_hour_amount || 0) +
          Number(t.extra_km_amount || 0);
        basicTotal += basic;
        battaTotal += Number(t.batta_main || 0);
        parkingTotal += Number(t.parking || 0);
        tollTotal += Number(t.toll || 0);
        permitTotal += Number(t.permit || 0);
        othersTotal += Number(t.others || 0);
        escortTotal += Number(t.escort_amount || 0);
        advanceTotal += Number(t.advance || 0);
        grossTotal +=
          basic +
          Number(t.batta_main || 0) +
          Number(t.parking || 0) +
          Number(t.toll || 0) +
          Number(t.permit || 0) +
          Number(t.others || 0) +
          Number(t.escort_amount || 0);
      });

      const contractRows = {
        Days: {
          limit: Number(invoiceContract.limit_days || 0),
          actual: Number(invoiceContract.customer_days || 0),
          extra: Number(invoiceContract.customer_extra_days || 0),
          rate: Number(invoiceContract.limit_day_charges || 0),
          total: Number(invoiceContract.limit_charges || 0),
        },
        Hours: {
          limit: Number(invoiceContract.limit_hrs || 0),
          actual: Number(invoiceContract.customer_hrs || 0),
          extra: Number(invoiceContract.customer_extra_hrs || 0),
          rate: Number(invoiceContract.limit_extra_hr_rate || 0),
          total: Number(invoiceContract.customer_extra_hrs_amount || 0),
        },
        Kms: {
          limit: Number(invoiceContract.limit_kms || 0),
          actual: Number(invoiceContract.customer_kms || 0),
          extra: Number(invoiceContract.customer_extra_kms || 0),
          rate: Number(invoiceContract.limit_extra_km_rate || 0),
          total: Number(invoiceContract.customer_extra_kms_amount || 0),
        },
      };

      let rowTitleDays = "Days";
      let rowTitleHours = "Hours";
      let rowTitleKms = "Kms";
      switch (invoiceContract.contract_bill_type) {
        case "Monthly Km Limit Month Rate":
          rowTitleHours = "Hours/Day";
          rowTitleKms = "Kms/Month";
          break;
        case "Monthly Hrs & Kms Limit Month Rate":
          rowTitleHours = "Hours/Month";
          rowTitleKms = "Kms/Month";
          break;
        case "Daily Km Limit Month Rate":
        case "Daily Km Limit Daily Rate":
          rowTitleHours = "Hours/Day";
          rowTitleKms = "Kms/Day";
          break;
      }

      const previewObject = {
        client,
        customer: customerDetails,
        rowTitles: { Days: rowTitleDays, Hours: rowTitleHours, Kms: rowTitleKms },
        invoice: {
          number: invoice.invoice_number,
          date: invoice.invoice_date,
          po_no: invoice.po_no,
          reference: invoice.reference,
        },
        trips: mappedTrips,
        rows: contractRows,
        totals: {
          before_tax: invoice.gross_before_tax || 0,
          taxableamount: invoice.taxable_amount || 0,
          cgst: invoice.cgst_amount || 0,
          sgst: invoice.sgst_amount || 0,
          igst: invoice.igst_amount || 0,
          cgst_percent: invoice.cgst_percent || 0,
          sgst_percent: invoice.sgst_percent || 0,
          igst_percent: invoice.igst_percent || 0,
          fuel_escalation: invoice.fuel_esc_amount || 0,
          fuel_ex_remarks: invoice.fuel_ex_remarks || "",
          any_addition: invoice.addition_amount || 0,
          addition_remarks: invoice.addition_remarks || "",
          any_deduction: invoice.deduction_amount || 0,
          deduction_remarks: invoice.deduction_remarks || "",
          discount: invoice.discount_amount || 0,
          discount_percent: invoice.discount_percent || 0,
          advance:
            (invoice.advance_amount || 0) + (invoice.voucher_advance_amount || 0),
          net: invoice.net_amount || 0,
          word_amount: numberToIndianWords(invoice.net_amount || 0),
          basic: basicTotal,
          batta: battaTotal,
          parking: parkingTotal,
          toll: tollTotal,
          permit: permitTotal,
          others: othersTotal,
          escort: escortTotal,
          breakup_advance: advanceTotal,
          gross: grossTotal,
          net_breakup: invoice.net_amount || 0,
        },
        gst: {
          gst_treatment: invoice.gst_treatment,
          gst_treatment_id: invoice.gst_treatment_id,
          gst_notification: invoice.gst_notification,
          hsn_code: invoice.hsn_code,
        },
        totalsBreakup: {
          basic: basicTotal,
          batta: battaTotal,
          parking: parkingTotal,
          toll: tollTotal,
          permit: permitTotal,
          others: othersTotal,
          escort: escortTotal,
          advance: advanceTotal,
          net: invoice.net_amount || 0,
          gross: grossTotal,
        },
        bank,
        terms: termsList,
      };

      const previewRes = await axiosInstance.post(
        "/api/invoice-template/preview/contract",
        previewObject,
      );

      setPreviewHTML(previewRes.data.html);
      setPreviewData(previewObject);
      setPreviewOpen(true);
    } else {
      // ---- LOCAL (default) ----
      const tripsRes = await axiosInstance.get(
        `/api/invoices/${row.invoiceid}/tripduties`,
        { params: { client_id: clientId, customer_id: invoice.customer_id } },
      );
      const payload = tripsRes.data;
      const selectedArray = Array.isArray(payload)
        ? payload
        : payload?.selected || [];
      const mappedTrips = selectedArray.map((t, i) =>
        mapTripDuty({ ...t, serial: i + 1 }),
      );

      let invoiceSettings = null;
      try {
        const settingsRes = await axiosInstance.get(
          `/api/clients/${clientId}/invoice-settings`,
        );
        invoiceSettings = settingsRes.data?.invoice || null;
      } catch (e) {
        invoiceSettings = null;
      }

      const previewObject = {
        template_id: invoiceSettings?.local_invoice_template_id || null,
        client,
        customer: customerDetails,
        invoice: {
          title: invoiceSettings?.invoice_title || "Invoice",
          number: invoice.invoice_number,
          date: invoice.invoice_date,
          po_no: invoice.po_no,
          reference: invoice.reference,
        },
        trips: mappedTrips,
        totals: {
          before_tax: invoice.gross_before_tax || 0,
          taxableamount: invoice.taxable_amount || 0,
          cgst: invoice.cgst_amount || 0,
          sgst: invoice.sgst_amount || 0,
          igst: invoice.igst_amount || 0,
          cgst_percent: invoice.cgst_percent || 0,
          sgst_percent: invoice.sgst_percent || 0,
          igst_percent: invoice.igst_percent || 0,
          fuel_escalation: invoice.fuel_esc_amount || 0,
          fuel_ex_remarks: invoice.fuel_ex_remarks || "",
          any_addition: invoice.addition_amount || 0,
          addition_remarks: invoice.addition_remarks || "",
          any_deduction: invoice.deduction_amount || 0,
          deduction_remarks: invoice.deduction_remarks || "",
          discount: invoice.discount_amount || 0,
          discount_percent: invoice.discount_percent || 0,
          advance:
            (invoice.advance_amount || 0) + (invoice.voucher_advance_amount || 0),
          net: invoice.net_amount || 0,
          word_amount: numberToIndianWords(invoice.net_amount || 0),
        },
        gst: {
          gst_treatment: invoice.gst_treatment,
          gst_treatment_id: invoice.gst_treatment_id,
          gst_notification: invoice.gst_notification,
          hsn_code: invoice.hsn_code,
        },
        bank,
        terms: termsList,
      };

      const previewRes = await axiosInstance.post(
        "/api/document-template/invoice/preview/local",
        previewObject,
      );

      setPreviewHTML(previewRes.data.html);
      setPreviewData(previewObject);
      setPreviewOpen(true);
    }
  } catch (err) {
    console.error("Print preview failed:", err.response || err);
    setSnackbar({
      open: true,
      msg: `Failed to build print preview: ${
        err.response?.data?.message || err.message || "Unknown error"
      }`,
      severity: "error",
    });
  } finally {
    setPrintLoading(false);
  }
};

  // Reset all filters
  const resetFilters = () => {
    setFilterStatus("All");
    setFilterInvoiceNumber("");
    setFilterFromDate(null);
    setFilterToDate(null);
    setFilterCustomer("");
  };

  // Paginate invoice data for display
  const pagedRows = invoices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const compactStyle = {
    minWidth: 140,
    height: 36,
    "& .MuiInputBase-root": {
      height: 36,
      fontSize: "0.75rem",
      padding: "2px 8px",
    },
  };

  const compactSelectStyle = {
    ...compactStyle,
    minWidth: 150,
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 100px)",
        p: 1,
        bgcolor: "#f8fafc",
      }}
    >
      {!showForm && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            mb: 1,
            alignItems: "center",
          }}
        >
          {/* Status filter */}
          <FormControl size="small" sx={compactStyle}>
            <InputLabel sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
              Status
            </InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
              MenuProps={{ PaperProps: { sx: { fontSize: "0.75rem" } } }}
              sx={{ height: 36, fontSize: "0.75rem" }}
            >
              {STATUS_FILTERS.map((st) => (
                <MenuItem key={st} value={st} sx={{ fontSize: "0.75rem" }}>
                  {st}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Invoice Number */}
          <TextField
            size="small"
            label="Invoice No"
            value={filterInvoiceNumber}
            onChange={(e) => setFilterInvoiceNumber(e.target.value)}
            sx={compactStyle}
          />

          {/* Customer */}
          <FormControl
            size="small"
            sx={{ ...compactSelectStyle, minWidth: 440 }}
          >
            <InputLabel sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
              Customer
            </InputLabel>
            <Select
              value={filterCustomer}
              label="Customer"
              onChange={(e) => setFilterCustomer(e.target.value)}
              MenuProps={{ PaperProps: { sx: { fontSize: "0.75rem" } } }}
              sx={{ height: 36, fontSize: "0.75rem" }}
            >
              <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                All
              </MenuItem>
              {customers.map((c) => (
                <MenuItem
                  key={c.customerid}
                  value={c.customerid}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {c.customername}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* From Date */}
          <TextField
            label="From Date"
            type="date"
            value={filterFromDate || ""}
            onChange={(e) => setFilterFromDate(e.target.value)}
            size="small"
            sx={{
              ...compactStyle,
              width: 140,
            }}
            InputLabelProps={{ shrink: true }}
          />

          {/* To Date */}
          <TextField
            label="To Date"
            type="date"
            value={filterToDate || ""}
            onChange={(e) => setFilterToDate(e.target.value)}
            size="small"
            sx={{
              ...compactStyle,
              width: 140,
            }}
            InputLabelProps={{ shrink: true }}
          />

          {/* Reset Filters */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={resetFilters}
            sx={{
              minWidth: 100,
              fontSize: "0.75rem",
              height: 32,
              minHeight: 32,
              lineHeight: "1rem",
              padding: "0 12px",
              boxSizing: "border-box",
            }}
          >
            RESET FILTERS
          </Button>

          <Box>
            <Button
              variant="contained"
              size="small"
              onClick={(e) => setAnchorElAdd(e.currentTarget)}
              sx={{
                minWidth: 100,
                fontSize: "0.75rem",
                height: 32,
                minHeight: 32,
                lineHeight: "1rem",
                padding: "0 8px",
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              ADD
              <ArrowDropDownIcon
                sx={{
                  fontSize: 18,
                  ml: 0.5,
                }}
              />
            </Button>

            <Menu
              anchorEl={anchorElAdd}
              open={Boolean(anchorElAdd)}
              onClose={() => setAnchorElAdd(null)}
            >
              <MenuItem
                onClick={() => {
                  setAnchorElAdd(null);
                  setShowForm({ type: "local", data: null });
                }}
                sx={{ fontSize: "0.75rem" }}
              >
                Local Invoice
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setAnchorElAdd(null);
                  setShowForm({ type: "contract", data: null });
                }}
                sx={{ fontSize: "0.75rem" }}
              >
                Contract Invoice
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setAnchorElAdd(null);
                  setShowForm({ type: "misc", data: null });
                }}
                sx={{ fontSize: "0.75rem" }}
              >
                Misc Invoice
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      )}

      {showForm ? (
        showForm.type === "local" ? (
          <InvoiceFormLocal
            editData={showForm.data}
            onCancel={() => setShowForm(false)}
            onSuccess={() => {
              setShowForm(false);
              setSnackbar({
                open: true,
                msg: "Invoice saved successfully",
                severity: "success",
              });
              fetchInvoices();
            }}
          />
        ) : showForm.type === "contract" ? (
          <InvoiceFormContract
            editData={showForm.data}
            onCancel={() => setShowForm(false)}
            onSuccess={() => {
              setShowForm(false);
              setSnackbar({
                open: true,
                msg: "Invoice saved successfully",
                severity: "success",
              });
              fetchInvoices();
            }}
          />
        ) : (
          <InvoiceFormMisc
            editData={showForm.data}
            onCancel={() => setShowForm(false)}
            onSuccess={() => {
              setShowForm(false);
              setSnackbar({
                open: true,
                msg: "Invoice saved successfully",
                severity: "success",
              });
              fetchInvoices();
            }}
          />
        )
      ) : (
        <Paper
          elevation={2}
          sx={{ borderRadius: 2, overflowX: "auto" }}
          ref={tableRef}
        >
          <Table size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5", height: 36 }}>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 105 }}
                >
                  Invoice No
                </TableCell>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 80 }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 65 }}
                >
                  Type
                </TableCell>

                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 160 }}
                >
                  Customer
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 80,
                    textAlign: "right",
                  }}
                >
                  Basic Amt
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 70,
                    textAlign: "right",
                  }}
                >
                  Batta
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 70,
                    textAlign: "right",
                  }}
                >
                  Parking
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 70,
                    textAlign: "right",
                  }}
                >
                  Others
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 70,
                    textAlign: "right",
                  }}
                >
                  Permit
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 70,
                    textAlign: "right",
                  }}
                >
                  Toll
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 60,
                    textAlign: "right",
                  }}
                >
                  Discount
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 80,
                    textAlign: "right",
                  }}
                >
                  Taxable Value
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 80,
                    textAlign: "right",
                  }}
                >
                  GST
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 90,
                    textAlign: "right",
                  }}
                >
                  Net Amount
                </TableCell>

                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 40 }}
                >
                  #Ts
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    width: 80,
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.length > 0 ? (
                pagedRows.map((row) => (
                  <TableRow key={row.invoiceid} hover>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.invoice_number}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.invoice_date
                        ? dayjs(row.invoice_date).format("DD/MM/YYYY")
                        : "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.bill_type || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.customer_name || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {row.basic_amount}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {row.batta_amount}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {row.parking_amount}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {row.others_amount}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {row.permit_amount}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {row.toll_amount}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {row.discount_amount}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {row.taxable_value}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {row.gst_amount}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", textAlign: "right" }}>
                      {row.net_amount}
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.no_of_tripsheets}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleRowMenuOpen(e, row)}
                        title="Actions"
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={15} align="center">
                    No invoices to display.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={invoices.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Rows per page"
            labelDisplayedRows={({ from, to, count }) =>
              `Showing ${from}-${to} of ${count}`
            }
            sx={{
              ".MuiTablePagination-toolbar": { minHeight: 36, height: 36 },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: 12,
                },
            }}
          />
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleRowMenuClose}
      >
        <MenuItem onClick={() => handleEdit(menuRow)} sx={{ fontSize: 13 }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handlePrint(menuRow)} sx={{ fontSize: 13 }}>
          <PrintIcon fontSize="small" sx={{ mr: 1 }} /> Print
        </MenuItem>
        <MenuItem
          onClick={() => handleDelete(menuRow)}
          sx={{ fontSize: 13, color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontSize: "0.95rem",
            fontWeight: 500,
            borderRadius: "8px",
            boxShadow: "0px 3px 8px rgba(0,0,0,0.25)",
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
            ...(snackbar.severity === "error" && {
              backgroundColor: "#fc0707ff",
            }),
            ...(snackbar.severity === "success" && {
              backgroundColor: "#04710cff",
            }),
          }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
      {previewOpen && previewHTML && (
        <PreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          html={previewHTML}
          invoiceData={previewData}
          mode="normal"
          showSnackbar={(snack) =>
            setSnackbar({
              open: true,
              msg: snack.message,
              severity: snack.severity,
            })
          }
        />
      )}
    </Box>
  );
}
